import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, UserMinus, UserX, Crown, Banknote, ShieldCheck,
  ShoppingBag, Image, Video, Radio, CalendarClock, Heart, Coins,
  Hourglass, TrendingUp, Wallet, ArrowDownToLine, BadgePercent,
  PlayCircle, Clock, Loader2, Calendar, Smartphone, MonitorSmartphone,
  Percent, Activity,
} from 'lucide-react';
import { Input } from './ui/input';
import api from '../api/client';

// Phase 33 — Extended Admin Overview KPI dashboard with time-range filter.
// Reads from /api/admin/overview.

const RANGES = [
  { key: 'today', label: 'Oggi' },
  { key: 'week',  label: 'Settimana' },
  { key: 'month', label: 'Mese' },
  { key: 'year',  label: 'Anno' },
  { key: 'all',   label: 'Tutto' },
  { key: 'custom',label: 'Personalizzato' },
];

const Tile = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

const SectionHeader = ({ children }) => (
  <h3 className="mt-6 mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
    {children}
  </h3>
);

import { fmtCredits } from '../lib/currency';

const AdminOverviewPanel = () => {
  const [range, setRange] = useState('all');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 47 · PWA vs browser telemetry — fixed 30-day window,
  // independent of the main range filter (opens are always volatile).
  const [pwaMetrics, setPwaMetrics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range };
      if (range === 'custom') {
        if (start) params.start = `${start}T00:00:00Z`;
        if (end) params.end = `${end}T23:59:59Z`;
      }
      const res = await api.get('/admin/overview', { params });
      setData(res.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, start, end]);

  useEffect(() => { load(); }, [load]);

  // Fire-and-forget PWA metrics fetch. Kept separate so an outage on
  // this endpoint never blocks the main KPI grid.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/telemetry/pwa', { params: { range_days: 30 } });
        if (!cancelled) setPwaMetrics(res.data);
      } catch (_) {
        if (!cancelled) setPwaMetrics(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="overview-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const u = data.users;
  const e = data.economy;
  const l = data.lives;

  return (
    <div className="space-y-2" data-testid="admin-overview-panel">
      {/* Range filter */}
      <div className="flex flex-wrap items-center gap-2" data-testid="overview-range-filter">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              range === r.key
                ? 'gold-gradient text-black'
                : 'border border-border text-muted-foreground hover:border-amber-500/40 hover:text-foreground'
            }`}
            data-testid={`overview-range-${r.key}`}
          >
            {r.label}
          </button>
        ))}
        {range === 'custom' && (
          <div className="ml-3 flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={start}
              onChange={(ev) => setStart(ev.target.value)}
              className="h-8 w-36 text-xs bg-background"
              data-testid="overview-start"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <Input
              type="date"
              value={end}
              onChange={(ev) => setEnd(ev.target.value)}
              className="h-8 w-36 text-xs bg-background"
              data-testid="overview-end"
            />
          </div>
        )}
      </div>

      {/* USERS */}
      <SectionHeader>Utenti</SectionHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Tile icon={Users}      label="Registrati"            value={u.total.toLocaleString()}              accent="bg-blue-500/20 text-blue-300"      testId="kpi-users-total" />
        <Tile icon={UserPlus}   label="Nuovi"                 value={u.new.toLocaleString()}                accent="bg-emerald-500/20 text-emerald-300" testId="kpi-users-new" />
        <Tile icon={UserX}      label="Eliminati"             value={u.deleted.toLocaleString()}            accent="bg-red-500/20 text-red-300"        testId="kpi-users-deleted" />
        <Tile icon={UserMinus}  label="Sospesi"               value={u.suspended.toLocaleString()}          accent="bg-amber-500/20 gold-text"         testId="kpi-users-suspended" />
        <Tile icon={Crown}      label="Voci Pro verificate"    value={u.verified_creators.toLocaleString()}  accent="bg-violet-500/20 text-violet-300"  testId="kpi-users-verified" />
        <Tile icon={Banknote}   label="Stripe Connect ok"     value={u.stripe_connect_done.toLocaleString()} accent="bg-cyan-500/20 text-cyan-300"     testId="kpi-users-stripe" />
        <Tile icon={ShieldCheck} label="KYC completato"       value={u.kyc_done.toLocaleString()}            accent="bg-teal-500/20 text-teal-300"     testId="kpi-users-kyc" />
      </div>

      {/* ECONOMY */}
      <SectionHeader>Economia</SectionHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Tile icon={ShoppingBag}   label="Ordini totali"            value={e.total_orders.toLocaleString()}                                              accent="bg-amber-500/20 gold-text"           testId="kpi-eco-orders" />
        <Tile icon={Image}         label="Post (img) venduti"       value={e.posts_image_sold.toLocaleString()}                                          accent="bg-blue-500/20 text-blue-300"        testId="kpi-eco-posts-image" />
        <Tile icon={Video}         label="Post (video) venduti"     value={e.posts_video_sold.toLocaleString()}                                          accent="bg-blue-500/20 text-blue-300"        testId="kpi-eco-posts-video" />
        <Tile icon={Radio}         label="Live ticket"              value={e.lives_sold.toLocaleString()}                                                accent="bg-red-500/20 text-red-300"          testId="kpi-eco-lives" />
        <Tile icon={CalendarClock} label="Scheduled Live"           value={e.scheduled_lives_sold.toLocaleString()}                                      accent="bg-orange-500/20 text-orange-300"    testId="kpi-eco-scheduled-lives" />
        <Tile icon={Heart}         label="Tip"                      value={e.tip_count.toLocaleString()}                                                 accent="bg-pink-500/20 text-pink-300"        testId="kpi-eco-tips" />
        <Tile icon={Coins}         label="Crediti acquistati"       value={fmtCredits(e.credits_purchased)}                                              accent="bg-emerald-500/20 text-emerald-300"  testId="kpi-eco-credits-purchased" />
        <Tile icon={TrendingUp}    label="Crediti spesi"            value={fmtCredits(e.credits_spent)}                                                  accent="bg-red-500/20 text-red-300"          testId="kpi-eco-credits-spent" />
        <Tile icon={Hourglass}     label="In maturazione"           value={fmtCredits(e.credits_maturing)}     sub="Saldo globale"                       accent="bg-amber-500/20 gold-text"           testId="kpi-eco-credits-maturing" />
        <Tile icon={Wallet}        label="Disponibili"              value={fmtCredits(e.credits_available)}    sub="Saldo globale"                       accent="bg-emerald-500/20 text-emerald-300"  testId="kpi-eco-credits-available" />
        <Tile icon={ArrowDownToLine} label="Payout richiesti"       value={fmtCredits(e.payouts_requested_amount)} sub={`${e.payouts_requested_count} richieste`} accent="bg-violet-500/20 text-violet-300" testId="kpi-eco-payouts-req" />
        <Tile icon={ArrowDownToLine} label="Payout completati"      value={fmtCredits(e.payouts_completed_amount)} sub={`${e.payouts_completed_count} eseguiti`}  accent="bg-emerald-500/20 text-emerald-300" testId="kpi-eco-payouts-done" />
        <Tile icon={BadgePercent}  label="Commissioni Nymia"        value={fmtCredits(e.platform_fees)}                                                  accent="bg-violet-500/20 text-violet-300"    testId="kpi-eco-platform-fees" />
        <Tile icon={Crown}         label="Chiamate"               value={e.one_to_one_sold.toLocaleString()} sub="Phase 32 in arrivo"                  accent="bg-muted text-muted-foreground"      testId="kpi-eco-one-to-one" />
      </div>

      {/* LIVES */}
      <SectionHeader>Live</SectionHeader>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile icon={PlayCircle} label="Live create"          value={l.total_created.toLocaleString()}                  accent="bg-blue-500/20 text-blue-300"      testId="kpi-lives-created" />
        <Tile icon={Radio}      label="Live concluse"         value={l.total_ended.toLocaleString()}                    accent="bg-red-500/20 text-red-300"        testId="kpi-lives-ended" />
        <Tile icon={Clock}      label="Minuti venduti"        value={`${l.minutes_sold.toLocaleString()} min`}          accent="bg-amber-500/20 gold-text"         testId="kpi-lives-minutes" />
        <Tile icon={Clock}      label="Minuti Chiamate"     value={`${l.one_to_one_minutes_sold.toLocaleString()} min`} sub="Phase 32 in arrivo" accent="bg-muted text-muted-foreground" testId="kpi-lives-one-to-one-min" />
      </div>

      {/* PWA TELEMETRY — Phase 47 · Fixed 30-day window */}
      <SectionHeader>Telemetria PWA · ultimi 30 giorni</SectionHeader>
      {pwaMetrics ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="pwa-telemetry-section">
          <Tile
            icon={Smartphone}
            label="Aperture da PWA installata"
            value={(pwaMetrics.pwa_opens || 0).toLocaleString()}
            sub={`Auth: ${(pwaMetrics.authenticated?.pwa_opens || 0).toLocaleString()} · Anon: ${(pwaMetrics.anonymous?.pwa_opens || 0).toLocaleString()}`}
            accent="bg-emerald-500/20 text-emerald-300"
            testId="kpi-pwa-standalone"
          />
          <Tile
            icon={MonitorSmartphone}
            label="Aperture da browser"
            value={(pwaMetrics.browser_opens || 0).toLocaleString()}
            sub={`Auth: ${(pwaMetrics.authenticated?.browser_opens || 0).toLocaleString()} · Anon: ${(pwaMetrics.anonymous?.browser_opens || 0).toLocaleString()}`}
            accent="bg-blue-500/20 text-blue-300"
            testId="kpi-pwa-browser"
          />
          <Tile
            icon={Percent}
            label="Utilizzo PWA"
            value={`${(pwaMetrics.pwa_percentage || 0).toFixed(1)}%`}
            sub="Adozione app installata"
            accent="bg-amber-500/20 gold-text"
            testId="kpi-pwa-percentage"
          />
          <Tile
            icon={Activity}
            label="Aperture totali"
            value={(pwaMetrics.total_opens || 0).toLocaleString()}
            sub="Sessioni tracciate"
            accent="bg-violet-500/20 text-violet-300"
            testId="kpi-pwa-total"
          />
        </div>
      ) : (
        <div
          className="rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground"
          data-testid="pwa-telemetry-empty"
        >
          Nessun dato di telemetria disponibile per gli ultimi 30 giorni.
        </div>
      )}
    </div>
  );
};

export default AdminOverviewPanel;
